import React, { useState, useEffect, useMemo, useRef } from 'react'
import logger from '@adc/logger'
import { Modal, Icon, Button, Text, SelectField } from '@adc/ui-morpheus'
import classnames from 'classnames/bind'
import type {
  CognitionEvaluationDataSet,
  CognitionEvaluationTaskName,
  CognitionEvaluationChatInputMessage,
  CognitionEvaluationJudge,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import {
  fetchDatasetsForTask,
  fetchDataset,
  updateDataset,
  createDataset,
  runDataset,
  fetchJudges,
} from '../../../providers/aiEvaluations'
import ContextSelector from '../../../components/contextSelector'
import type { ContextOutput } from '../../../components/contextSelector'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface DatasetCaseProps {
  input: string
  expectedOutput: string
}

const ReadOnlyCase = ({ input, expectedOutput }: DatasetCaseProps) => (
  <tr className={cx('data-row')}>
    <td width='50%'>
      <Text size='small'>{input}</Text>
    </td>
    <td width='50%'>
      <Text size='small'>{expectedOutput}</Text>
    </td>
  </tr>
)

/**
 * Currently on supports datasets with CognitionEvaluationChatInputMessage inputs
 * and string expected outputs.
 */
const getCasesFromDataset = (
  dataset: CognitionEvaluationDataSet
): Array<{ input: string; expectedOutput: string }> => {
  if (
    !('cases' in dataset) ||
    !dataset?.cases ||
    !Array.isArray(dataset.cases)
  ) {
    return []
  }
  return dataset.cases.map(caseItem => ({
    input:
      (caseItem.input as CognitionEvaluationChatInputMessage)?.object
        ?.message || '',
    expectedOutput: caseItem.output_description || '',
  }))
}

const getJudgeUrnsFromDataset = (
  dataset: CognitionEvaluationDataSet
): string[] => dataset.judges_urns ?? []

interface ManageAndRunDatasetsModalProps {
  task: CognitionEvaluationTaskName
  isOpen: boolean
  onClose: () => void
}

interface Case {
  input: string
  expectedOutput: string
}

type ModalMode = 'read' | 'edit' | 'create' | 'run'

const ManageAndRunDatasetsModal: React.FC<ManageAndRunDatasetsModalProps> = ({
  isOpen,
  onClose,
  task,
}): React.ReactElement => {
  const [datasets, setDatasets] = useState<CognitionEvaluationDataSet[]>([])
  const [selectedDatasetUrn, setSelectedDatasetUrn] = useState<string>('')

  const [judges, setJudges] = useState<CognitionEvaluationJudge[]>([])

  const initialSelectedJudgeUrns = useRef<CognitionEvaluationJudge['urn'][]>([])
  const [selectedJudgeUrns, setSelectedJudgeUrns] = useState<
    CognitionEvaluationJudge['urn'][]
  >([])

  const [isLoading, setIsLoading] = useState(false)
  const [cases, setCases] = useState<Case[]>([])
  const [isCasesLoading, setIsCasesLoading] = useState(false)

  // Edit mode state
  const [mode, setMode] = useState<ModalMode>('read')
  const [editableCases, setEditableCases] = useState<Case[]>([])
  const [editableDescription, setEditableDescription] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const lastCaseInputRef = useRef<HTMLTextAreaElement>(null)
  const shouldFocusNewCase = useRef(false)

  // Run mode state
  const [selectedContext, setSelectedContext] = useState<ContextOutput | null>(
    null
  )
  const [isRunning, setIsRunning] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      fetchDatasetsForTask(task)
        .then(data => {
          setDatasets(data)
          return null
        })
        .catch(() => {
          setDatasets([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setSelectedDatasetUrn('')
      setCases([])
      setMode('read')
      setEditableCases([])
      setEditableDescription('')
      setIsDirty(false)
      setSelectedContext(null)
      setSelectedJudgeUrns([])
      initialSelectedJudgeUrns.current = []
    }
  }, [isOpen, task])

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      fetchJudges(task)
        .then(data => {
          setJudges(data)
          return null
        })
        .catch(() => {
          setJudges([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setJudges([])
      setSelectedJudgeUrns([])
      initialSelectedJudgeUrns.current = []
    }
  }, [isOpen, task])

  useEffect(() => {
    if (selectedDatasetUrn) {
      setIsCasesLoading(true)
      fetchDataset(selectedDatasetUrn)
        .then(data => {
          setCases(getCasesFromDataset(data))
          const judgeUrns = getJudgeUrnsFromDataset(data)
          setSelectedJudgeUrns(judgeUrns)
          initialSelectedJudgeUrns.current = judgeUrns
          return null
        })
        .catch(() => {
          setCases([])
        })
        .finally(() => {
          setIsCasesLoading(false)
        })
    } else {
      setCases([])
    }
  }, [selectedDatasetUrn, task, judges])

  // Focus new case input after render
  useEffect(() => {
    if (shouldFocusNewCase.current && lastCaseInputRef.current) {
      lastCaseInputRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      lastCaseInputRef.current.focus()
      shouldFocusNewCase.current = false
    }
  }, [editableCases.length])

  const handleEditClick = () => {
    const currentDataset = datasets.find(d => d.urn === selectedDatasetUrn)
    setEditableCases([...cases])
    setEditableDescription(currentDataset?.description || '')
    setIsDirty(false)
    setMode('edit')
  }

  const handleCreateClick = () => {
    setEditableCases([])
    setEditableDescription('')
    setIsDirty(false)
    setMode('create')
  }

  const handleRunClick = () => {
    setSelectedContext(null)
    setMode('run')
  }

  const handleRunDataset = () => {
    setIsRunning(true)

    const context: {
      sources: Array<string>
      run_as?: string
    } = {
      sources: selectedContext?.sources || [],
    }

    if (selectedContext?.run_as) {
      context.run_as = selectedContext.run_as
    }

    runDataset({
      task,
      dataSetURN: selectedDatasetUrn,
      context,
    })
      .then(() => {
        setShowSuccessModal(true)
        setSelectedContext(null)
        return null
      })
      .catch(error => {
        logger.error('Failed to run dataset:', error)
      })
      .finally(() => {
        setIsRunning(false)
      })
  }

  const handleCaseChange = (
    index: number,
    field: 'input' | 'expectedOutput',
    value: string
  ) => {
    setEditableCases(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
    setIsDirty(true)
  }

  const handleDescriptionChange = (value: string) => {
    setEditableDescription(value)
    setIsDirty(true)
  }

  const handleDeleteCase = (index: number) => {
    setEditableCases(prev => prev.filter((_, i) => i !== index))
    setIsDirty(true)
  }

  const handleAddCase = () => {
    setEditableCases(prev => [...prev, { input: '', expectedOutput: '' }])
    setIsDirty(true)
    shouldFocusNewCase.current = true
  }

  const handleSave = () => {
    // Filter out cases with empty input (expectedOutput can be empty)
    const validCases = editableCases.filter(c => c.input.trim() !== '')

    // If no valid cases remain, update state and stay in edit/create mode
    if (validCases.length === 0) {
      setEditableCases([])
      return
    }

    // Validate description is not empty
    if (!editableDescription.trim()) {
      logger.error('Dataset description cannot be empty')
      return
    }

    // Transform cases to the format expected by the API
    const taskKey = task.toLowerCase()
    const transformedCases = validCases.map(caseItem => ({
      input: {
        [taskKey]: {
          type: 'object' as const,
          visibility: 'PUBLIC' as const,
          object: {
            message: caseItem.input,
          },
        },
      },
      output_description: caseItem.expectedOutput,
    }))

    const judgesUrns = selectedJudgeUrns

    // Save the dataset (create or update)
    setIsCasesLoading(true)
    const savePromise =
      mode === 'create'
        ? createDataset({
            task,
            description: editableDescription.trim(),
            cases: transformedCases,
            judges_urns: judgesUrns,
          })
        : updateDataset({
            urn: selectedDatasetUrn,
            task,
            description: editableDescription.trim(),
            cases: transformedCases,
            judges_urns: judgesUrns,
          })

    savePromise
      .then(result => {
        setMode('read')
        setCases(validCases)
        setIsDirty(false)
        // If we created a new dataset, select it
        if (mode === 'create' && result.urn) {
          setSelectedDatasetUrn(result.urn)
        }
        // Refetch datasets to update the dropdown
        return fetchDatasetsForTask(task)
      })
      .then(data => {
        if (data) {
          setDatasets(data)
        }
        return null
      })
      .catch(error => {
        logger.error('Failed to save dataset:', error)
        // Stay in edit/create mode on error
      })
      .finally(() => {
        setIsCasesLoading(false)
      })
  }

  const handleCancelEdit = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      setMode('read')
    }
  }

  const handleSelectJudge = (_e: unknown, data: { value: string[] }) => {
    setIsDirty(true)
    setSelectedJudgeUrns(data.value)
  }

  const handleConfirmDiscard = () => {
    // If we were in create mode, clear the selection since there's nothing to show
    const wasCreating = mode === 'create'

    setShowDiscardConfirm(false)
    setMode('read')
    setEditableCases([])
    setEditableDescription('')
    setIsDirty(false)

    if (wasCreating) {
      setSelectedDatasetUrn('')
      setCases([])
    }

    setSelectedJudgeUrns(initialSelectedJudgeUrns.current)
  }

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setMode('read')
  }

  const handleCloseModal = () => {
    if ((mode === 'edit' || mode === 'create') && isDirty) {
      setShowDiscardConfirm(true)
    } else {
      onClose()
    }
  }

  const datasetOptions = useMemo(
    () =>
      datasets
        .map(dataset => ({
          text: dataset.description,
          value: dataset.urn,
        }))
        .sort((a, b) => {
          const textA = (a.text || '').toLowerCase()
          const textB = (b.text || '').toLowerCase()
          return textA.localeCompare(textB)
        }),
    [datasets]
  )

  const judgeOptions = useMemo(
    () =>
      judges
        .sort((a, b) => {
          const textA = (a.description || '').toLowerCase()
          const textB = (b.description || '').toLowerCase()
          return textA.localeCompare(textB)
        })
        .map(judge => ({
          text: `${judge.description}${
            judge.type === graph.enums.CognitionEvaluationJudgeType.Prompt
              ? ' (LLM)'
              : ''
          }`,
          value: judge.urn,
        })),
    [judges]
  )

  const renderReadMode = () => (
    <>
      <Modal.Header>
        <Text size='huge' weight='heavy'>
          Manage and Run Datasets
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div className={cx('content-wrapper')}>
          <div className={cx('dataset-row')}>
            <div className={cx('dataset-selector')}>
              <Text size='small' color='gray' className={cx('label')}>
                Dataset
              </Text>
              <SelectField
                hideWrapper
                value={selectedDatasetUrn}
                onChange={(_e: unknown, data: { value: string }) =>
                  setSelectedDatasetUrn(data.value)
                }
                options={datasetOptions}
                placeholder='Select a Dataset'
                compact
                elmId='dataset_selector'
                loading={isLoading}
                search
              />
            </div>
            {selectedDatasetUrn && (
              <div className={cx('dataset-selector')}>
                <Text size='small' color='gray' className={cx('label')}>
                  Judge Module(s)
                </Text>

                <SelectField
                  hideWrapper
                  viewMode
                  multiple
                  value={selectedJudgeUrns}
                  onChange={handleSelectJudge}
                  options={judgeOptions}
                  placeholder='Select judge modules'
                  compact
                  elmId='judge_selector'
                  loading={isLoading}
                  search
                />
              </div>
            )}
          </div>

          {selectedDatasetUrn ? (
            <div className={cx('table-placeholder')}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {isCasesLoading ? (
                <div className={cx('zero-state')}>
                  <Icon
                    size='huge'
                    name='spinner'
                    iconStyle='light'
                    color='grayMedium'
                    loading
                  />
                  <Text color='grayMedium'>Loading cases...</Text>
                </div>
              ) : cases.length > 0 ? (
                <table className={cx('data-table')}>
                  <thead>
                    <tr>
                      <th>
                        <Text size='small' weight='heavy'>
                          Input
                        </Text>
                      </th>
                      <th>
                        <Text size='small' weight='heavy'>
                          Expected Output
                        </Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map((caseItem, index) => (
                      <ReadOnlyCase
                        key={`case_${index}`}
                        input={caseItem.input}
                        expectedOutput={caseItem.expectedOutput}
                      />
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={cx('zero-state')}>
                  <Icon
                    size='huge'
                    name='table'
                    iconStyle='light'
                    color='grayMedium'
                  />
                  <Text color='grayMedium'>
                    No cases found for this dataset
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <div className={cx('zero-state')}>
              <Icon
                size='huge'
                name='table'
                iconStyle='light'
                color='grayMedium'
              />
              <Text color='grayMedium'>
                Select a dataset to view, edit, or run
              </Text>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <div className={cx('actions')}>
          <Button onClick={onClose} elmId='modal_close_button' size='small'>
            Close
          </Button>
          <Button
            onClick={handleCreateClick}
            elmId='modal_create_button'
            size='small'
          >
            Create
          </Button>
          <Button
            onClick={handleEditClick}
            role='primary'
            elmId='modal_edit_button'
            size='small'
            disabled={!selectedDatasetUrn}
          >
            Edit
          </Button>
          <Button
            onClick={handleRunClick}
            role='primary'
            elmId='modal_run_button'
            size='small'
            disabled={!selectedDatasetUrn}
          >
            Run
          </Button>
        </div>
      </Modal.Actions>
    </>
  )

  const renderEditMode = () => (
    <>
      <Modal.Header>
        <Text size='huge' weight='heavy'>
          {mode === 'create' ? 'Create Dataset' : 'Edit Dataset'}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div className={cx('content-wrapper')}>
          <div className={cx('dataset-row')}>
            <div className={cx('dataset-selector', 'ui form')}>
              <Text size='small' color='gray' className={cx('label')}>
                Dataset (Description)
              </Text>
              <input
                type='text'
                value={editableDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleDescriptionChange(e.target.value)
                }
                placeholder='Enter dataset description...'
                data-elm-id='dataset_description_input'
                className={cx('description-input')}
              />
            </div>
            <div className={cx('dataset-selector')}>
              <Text size='small' color='gray' className={cx('label')}>
                Judge Module(s)
              </Text>
              <SelectField
                hideWrapper
                multiple
                value={selectedJudgeUrns}
                onChange={handleSelectJudge}
                options={judgeOptions}
                placeholder='Select judge modules'
                compact
                elmId='judge_selector'
                loading={isLoading}
                search
              />
            </div>
          </div>

          <div className={cx('cases-section')}>
            <div className={cx('cases-header')}>
              <Text size='small' color='gray' className={cx('label')}>
                Cases
              </Text>
              <Button
                onClick={handleAddCase}
                elmId='add_case_button'
                size='small'
                icon='plus'
              >
                Add Case
              </Button>
            </div>

            {editableCases.length > 0 ? (
              <div className={cx('editable-cases-container')}>
                <div className={cx('editable-cases-header')}>
                  <Text
                    size='small'
                    color='gray'
                    className={cx('column-label')}
                  >
                    Input
                  </Text>
                  <Text
                    size='small'
                    color='gray'
                    className={cx('column-label')}
                  >
                    Expected Output
                  </Text>
                  <div className={cx('delete-spacer')} />
                </div>
                {editableCases.map((caseItem, index) => (
                  <div
                    key={`editable_case_${index}`}
                    className={cx('editable-case-row', 'ui form')}
                  >
                    <textarea
                      ref={
                        index === editableCases.length - 1
                          ? lastCaseInputRef
                          : null
                      }
                      value={caseItem.input}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        handleCaseChange(index, 'input', e.target?.value)
                      }}
                      placeholder='Enter input...'
                      data-elm-id={`case_input_${index}`}
                      className={cx('case-textarea')}
                      rows={2}
                    />
                    <textarea
                      value={caseItem.expectedOutput}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        handleCaseChange(
                          index,
                          'expectedOutput',
                          e.target?.value
                        )
                      }}
                      placeholder='Enter expected output...'
                      data-elm-id={`case_expected_output_${index}`}
                      className={cx('case-textarea')}
                      rows={2}
                    />
                    <button
                      type='button'
                      className={cx('delete-case-button')}
                      onClick={() => handleDeleteCase(index)}
                      onKeyDown={e => {
                        if (
                          e.key === 'Tab' &&
                          !e.shiftKey &&
                          index === editableCases.length - 1
                        ) {
                          e.preventDefault()
                          handleAddCase()
                        }
                      }}
                      aria-label='Delete case'
                    >
                      <Icon name='trash' iconStyle='solid' color='error' />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={cx('zero-state')}>
                <Icon
                  size='huge'
                  name='table'
                  iconStyle='light'
                  color='grayMedium'
                />
                <Text color='grayMedium'>Click Add Case to add cases</Text>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <div className={cx('actions')}>
          <Button
            onClick={handleCancelEdit}
            elmId='modal_cancel_button'
            size='small'
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            role='primary'
            elmId='modal_save_button'
            size='small'
            disabled={editableCases.length === 0 || !editableDescription.trim()}
          >
            Save
          </Button>
        </div>
      </Modal.Actions>
    </>
  )

  const renderRunMode = () => {
    const currentDataset = datasets.find(d => d.urn === selectedDatasetUrn)

    return (
      <>
        <Modal.Header>
          <Text size='huge' weight='heavy'>
            Run Dataset
          </Text>
        </Modal.Header>
        <Modal.Content>
          <div className={cx('content-wrapper')}>
            <div className={cx('dataset-row')}>
              <div className={cx('dataset-selector')}>
                <Text size='small' color='gray' className={cx('label')}>
                  Dataset
                </Text>
                <Text>{currentDataset?.description || ''}</Text>
              </div>
            </div>

            <ContextSelector
              onContextChange={setSelectedContext}
              elmId='dataset_context_selector'
              enableRunAs
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <div className={cx('actions')}>
            <Button
              onClick={() => setMode('read')}
              elmId='modal_cancel_button'
              size='small'
            >
              Cancel
            </Button>
            <Button
              onClick={handleRunDataset}
              role='primary'
              elmId='modal_continue_button'
              size='small'
              loading={isRunning}
              disabled={isRunning || !selectedContext}
            >
              Continue
            </Button>
          </div>
        </Modal.Actions>
      </>
    )
  }

  const renderModalContent = () => {
    if (mode === 'read') return renderReadMode()
    if (mode === 'run') return renderRunMode()
    return renderEditMode()
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleCloseModal}
        size='large'
        elmId='manage_and_run_datasets_modal'
        closeIcon={
          <Icon
            className='close'
            data-elm-id='manage_and_run_datasets_modal_close_button'
            name='times'
            iconStyle='regular'
            fitted
            onClick={handleCloseModal}
          />
        }
        className={cx('modal')}
        closeOnDimmerClick={false}
      >
        {renderModalContent()}
      </Modal>

      <Modal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        size='tiny'
        elmId='discard_changes_modal'
      >
        <Modal.Header>
          <Text size='large' weight='heavy'>
            Discard Changes?
          </Text>
        </Modal.Header>
        <Modal.Content>
          <Text>Are you sure? All changes will be lost.</Text>
        </Modal.Content>
        <Modal.Actions>
          <div className={cx('actions')}>
            <Button
              onClick={() => setShowDiscardConfirm(false)}
              elmId='discard_cancel_button'
              size='small'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDiscard}
              role='primary'
              elmId='discard_confirm_button'
              size='small'
            >
              Discard
            </Button>
          </div>
        </Modal.Actions>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        size='tiny'
        elmId='dataset_success_modal'
      >
        <Modal.Header>
          <Text size='huge' weight='heavy'>
            Your dataset is running!
          </Text>
        </Modal.Header>
        <Modal.Content>
          <div className={cx('success-panel')}>
            <Icon
              size='huge'
              name='party-horn'
              iconStyle='solid'
              color='sunYellow'
            />
            <Text color='gray' style={{ textAlign: 'center' }}>
              Sit tight, results will show up shortly in the data table.
            </Text>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={handleCloseSuccessModal}
            role='primary'
            elmId='success_modal_close_button'
            size='small'
          >
            Got it!
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default ManageAndRunDatasetsModal
